import * as yup from 'yup';
import { ValidationError } from 'yup';

import { AssignContactInput } from '../../../../../../graphql';
import { SalesAndTradingIndicationQuery } from '../../context/hooks/sales-and-trading/__generated__';
import { IndicationForm_IndicationPartsFragment } from '../indication-form/graphql/__generated__';
import { ManageIndication_MyIndicationPartsFragment } from '../manage-indication/graphql/__generated__';
import { InvestorContactInformation_ContactCollectionPartsFragment } from './graphql/__generated__';

export type InvestorContact = {
  name: string;
  email: string;
  isCustom: boolean;
};

export type InvestorContactValues = {
  buySideContacts: InvestorContact[];
  sellSideContacts: InvestorContact[];
};

export const CONTACT_REQUIRED = 'CONTACT_REQUIRED';
export const UNIQUE_CONTACTS = 'UNIQUE_CONTACTS';

export const getDuplicateCustomInvestorContactIndexes = (contacts: InvestorContact[]): number[] =>
  contacts.reduce<number[]>(
    (acc, { name, email, isCustom }, index) =>
      isCustom &&
      contacts.filter(contact => contact.name === name && contact.email === email).length > 1
        ? [...acc, index]
        : acc,
    []
  );

export type ContactsArraySchemaContext = {
  isInvestorContactRequired?: boolean;
  createMode?: boolean;
  displayMissingContacts?: boolean;
};

export const contactsArraySchema = yup
  .array()
  .label('Investor Contacts')
  .test(
    'contact-required',
    CONTACT_REQUIRED,
    (value: InvestorContact[] | undefined, { options, parent }) => {
      const { isInvestorContactRequired, createMode, displayMissingContacts } =
        (options.context as ContactsArraySchemaContext) ?? {};
      const buySideContacts: InvestorContact[] = parent?.buySideContacts ?? [];

      if (!isInvestorContactRequired) return true;

      const hasBuySide = buySideContacts.some(c => c.name && c.email);
      const hasSellSide = value?.some(c => c.name && c.email) ?? false;

      if (createMode) {
        return hasBuySide || hasSellSide;
      }

      return hasBuySide || !displayMissingContacts;
    }
  )
  .test(
    'unique-contacts',
    UNIQUE_CONTACTS,
    // Valid Yup test function type
    // eslint-disable-next-line sonarjs/function-return-type
    (value: InvestorContact[] | undefined, { path, parent }) => {
      if (value === undefined || value.length === 0) {
        return true;
      }

      const errorFieldIndexes = getDuplicateCustomInvestorContactIndexes([
        ...value,
        ...(parent?.buySideContacts ?? []),
      ]);

      if (errorFieldIndexes.length === 0) {
        return true;
      }

      return new ValidationError(
        errorFieldIndexes
          .map(index => [
            new ValidationError('Contact is not unique', '', `${path}.${index}.name`),
            new ValidationError('Contact is not unique', '', `${path}.${index}.email`),
          ])
          .flat()
      );
    }
  )
  .of(
    yup.object().shape(
      {
        name: yup
          .string()
          .max(255)
          .label('Contact Name')
          .required('Contact Name is a required field')
          .when('email', {
            is: email => email && email.length > 0,
            then: schema => schema.required(),
          }),
        email: yup
          .string()
          .max(255)
          .email()
          .label('Email Address')
          .required('Email Address is a required field')
          .when('name', {
            is: name => name && name.length > 0,
            then: schema => schema.required(),
          }),
      },
      [['name', 'email']]
    )
  );

export const contactsValidationSchema = yup.object().shape({
  sellSideContacts: contactsArraySchema,
});

export function mapToFormValues(
  item: InvestorContactInformation_ContactCollectionPartsFragment['contacts'][number]
): InvestorContact {
  return {
    email: item.email,
    name: item.name,
    isCustom: false,
  };
}

export function mapValuesToInput(values: InvestorContact[]): AssignContactInput[] {
  return values
    .filter(item => item.email !== '' && item.name !== '') // Ignore empty rows
    .map(item => ({
      email: item.email,
      name: item.name,
    }));
}

export type InstitutionalIndicationType =
  | ManageIndication_MyIndicationPartsFragment
  | IndicationForm_IndicationPartsFragment
  | NonNullable<SalesAndTradingIndicationQuery['coveredIndication']>;
